import instantsearch from 'instantsearch.js';
import {
  configure,
  hits,
  clearRefinements,
  refinementList,
  stats,
  pagination,
} from 'instantsearch.js/es/widgets';
import searchClient from './searchClient';
import SearchPage from './search-page';

const containers = {
  stats: '#projects-search-stats',
  hitsContainer: '#projects-search-hits-container',
  hits: '#projects-search-hits',
  pagination: '#projects-search-pagination',
  refinementListProjectTypes: '#projects-search-refinement-list-project-types',
  refinementListDepartments: '#projects-search-refinement-list-department',
  refinementListResearchCenters: '#projects-search-refinement-list-research-centers',
  refinementListTopics: '#projects-search-refinement-list-topics',
  clearRefinements: '#projects-search-clear-refinements',
};

class AlgoliaProjectsSearch extends SearchPage {
  constructor(indexName, department = null, researchCenter = null) {
    super(indexName);

    this.indexName = indexName;

    // Pre-filter parameters
    this.department = department;
    this.departmentFilter = this.department ? ` AND departments:"${this.department}"` : '';

    this.researchCenter = researchCenter;
    this.researchCenterFilter = this.researchCenter
      ? ` AND research_centers:"${this.researchCenter}"`
      : '';

    this.hitsPerPage = 24;

    this.search = instantsearch({
      indexName: this.indexName,
      routing: true,
      searchClient,
      // https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-searchfunction
      searchFunction: helper => {
        helper.search();
      },
    });

    this.initialize();
    this.search.start();
  }

  initialize() {
    // https://www.algolia.com/doc/api-reference/widgets/js/
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter the query with facet type of project
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: `(type:project)${this.departmentFilter}${this.researchCenterFilter}`,
      }),

      // https://www.algolia.com/doc/api-reference/widgets/stats/js/
      stats({
        container: containers.stats,
        templates: {
          text: data => {
            let count = '';

            const pagination = document.querySelector(containers.pagination);
            this.toggleHide(pagination, data.nbHits <= this.hitsPerPage);

            if (data.hasManyResults) {
              count += `${data.nbHits} results found`;
            } else if (data.hasOneResult) {
              count += `1 result found`;
            } else {
              count += `<span class="search__stats-no-results">No results found.</span>`;
            }

            return `${count}`;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: containers.hits,
        escapeHTML: false,
        templates: {
          /* eslint-disable */
          item(hit) {
            return `
              <a href="${hit.url}" class="hit-link">
                ${
                  hit['featured_image']
                    ? `
                  <div class="hit-image">
                    <img class="" src="${hit['featured_image']}" alt="${hit.title}" />
                  </div>
                  `
                    : `
                  <div class="hit-placeholder-image-wrapper">
                    <div class="hit-placeholder-image">
                      <svg>
                        <use xlink:href="#ne-n"></use>
                      </svg>
                    </div>
                  </div>
                `
                }
                <h2 class="hit-title">
                  ${instantsearch.highlight({ attribute: 'title', hit })}
                </h2>
              </a>
            `;
          },
          /* eslint-enable */
        },
        transformItems: items => {
          // Hide pagination and results if no results
          this.toggleHide(this.results, items.length < 1);
          if (window.innerWidth <= 768 && items.length > 0) {
            this.toastActive();
          }
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      pagination({
        container: containers.pagination,
        showFirst: false,
        showLast: false,
        scrollTo: '.js--projects-search',
        templates: {
          previous: `
            <span class="visually-hidden">Previous page</span>
            <svg class="arrow prev">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
          next: `
            <span class="visually-hidden">Next page</span>
            <svg class="arrow next">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
        },
      }),

      // Project Types
      refinementList({
        container: containers.refinementListProjectTypes,
        attribute: 'project_types',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListProjectTypes)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // School or Department
      refinementList({
        container: containers.refinementListDepartments,
        attribute: 'departments',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if department is specified
          const refList = document
            .querySelector(containers.refinementListDepartments)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.department || items.length < 1);
          return items;
        },
      }),

      // Research Centers
      refinementList({
        container: containers.refinementListResearchCenters,
        attribute: 'research_centers',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if research center is specified
          const refList = document
            .querySelector(containers.refinementListResearchCenters)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.researchCenter || items.length < 1);
          return items;
        },
      }),

      // Topics
      refinementList({
        container: containers.refinementListTopics,
        attribute: 'topics',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListTopics)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/clear-refinements/js/
      clearRefinements({
        container: containers.clearRefinements,
        templates: {
          resetLabel: 'Clear filters',
        },
      }),
    ]);
  }

  static init() {
    new AlgoliaProjectsSearch();
  }
}

export default AlgoliaProjectsSearch;
